import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Image,
  Link,
  ListItem,
  Stack,
  Switch,
  Text,
  Tooltip,
  UnorderedList,
  useDisclosure,
  useTheme
} from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { OfferPackage, OfferProperty, OfferTravel, PackageItemModel, PartPackage, PartPackageProperty, Variant } from 'BrokerTsLib/src/dtos'
import { OfferPackageSettingsFormTabs } from 'BrokerTsLib/src/dtos'
import { Product, Risk } from 'BrokerTsLib/src/enums'
import { OfferStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import type { IBodyCell } from 'BrokerTsLib/src/interfaces/offers/grid/columnSections'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Apis } from '@/appSettings'
import { EditableTinySelect, scrollToOffer, useLoadingOverlay } from '@/components'
import { ConfirmModal } from '@/components/ConfirmModal'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useOfferGrid } from '@/context/OfferGridContext'
import { usePrintContext } from '@/context/PrintContext'
import { useOffersContext } from '@/context/useOffersContext'
import { useGetGroupedRisks, useUrl } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useGetOfferDocuments } from '@/hooks/useDocuments'
import { useClone, useGetOfferAvailableDiscounts, useSelectCalculation, useSetOfferIncluded } from '@/hooks/useOffer'
import { useGetAvailablePackages, useGetGrouppedAvailablePackages, useGetOfferAvailableAddons, useGetOfferAvailablePackageItems, useSaveOfferParts, useUpdateOfferPackage } from '@/hooks/useOfferPackage'
import { useUpdateLastWizardStep } from '@/hooks/useWizard'
import { Check, Duplicate, EditFilled, Pdf, Refresh, Settings } from '@/icons'
import { GetWizardProductPrefix } from '@/pages/steps/vehicle/4'
import { getLogo } from '@/utils/companiesLogos'
import { formatDateTime } from '@/utils/formatDate'
import { getQuoteId } from '@/utils/getQuoteId'

import { OfferCell } from '../OfferCell'
import { OfferCellPayment } from '../OfferCellPayment'
import { BlankOfferCell, OfferCellWrapper } from '../OfferCellWrapper'
import { OfferPackageColumnSection } from '../package/OfferPackageColumnSection'
import { OfferPackageSettingsForm } from '../package/OfferPackageSettingsForm'
import { StatusBadge } from '../StatusBadge'

interface IDictionary {
  [index: string]: string
}

export const offerStatusColors: IDictionary = {
  valid: 'gray.100',
  selected: 'green.100',
  error: 'red.100',
}


const OfferColumnSection = ({ children, sx, ...rest }: BoxProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 2,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

export interface OfferProps {
  offer: OfferPackage,
  onToggle?: (included: boolean) => void,
  variant: Variant
}

export const OfferPackageColumn = ({ offer, onToggle, variant }: OfferProps) => {
  const offerProperty = offer as OfferProperty
  const offerTravel = offer as OfferTravel
  const { product } = useApp()
  const isLiability = product == Product.LiabilityInsurance
  const isProperty = product == Product.PropertyInsurance
  const isTravel = product == Product.TravelInsurance
  const navigate = useNavigate()

  const { user } = useAuth()
  const theme = useTheme()
  const { printMode } = usePrintContext()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentLiabilityPackageId, setCurrentLiabilityPackageId] = useState(offer.liabilityPackageId)
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure()

  const documentUrl = useUrl(Apis.document)

  const { insuranceParamsId, insuranceParams, refetchInsuranceParams, isDisabled: ipIsDisabled } = useInsuranceParams()
  const { updateOffer, calculatingOffersIds, recalculateOffers: recalculate } = useOffersContext()
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false
  const { setLoading, startAnimation } = useLoadingOverlay()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: selectCalculation } = useSelectCalculation()
  const insuranceGroups = useGetGroupedRisks(variant)
  const { mutateAsync: saveOfferParts } = useSaveOfferParts()
  const { mutateAsync: cloneOffer } = useClone(offer.offerId)
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { openedColumns, onSectionTitleClick } = useOfferGrid()

  const { data: availablePackagesData } = useGetAvailablePackages(offer.offerId, 0)
  const { mutateAsync: updatePackage } = useUpdateOfferPackage()

  const availablePackages = availablePackagesData?.data.map(x => { return { value: x.name, id: x.id, groupId: x.value } }) ?? []
  const groupedAvailablePackages = useGetGrouppedAvailablePackages(offer.offerId)

  const hasAnyPackageToChange = Object.values(groupedAvailablePackages).some(group => group.length > 1)

  const isDisabled = ipIsDisabled || printMode
  const displayId = `${offer.offerId}/${insuranceParamsId}`

  const hasError = (offer.errors?.length ?? 0) > 0
  const errMsg = hasError ? offer.errors?.map(e => e.message).join('<br/>') : ''

  let status = OfferStatusEnum.Valid
  if (hasError) status = OfferStatusEnum.Error
  else if (insuranceParams?.generalParams.selectedOfferId === offer.offerId) status = OfferStatusEnum.Selected

  const color = offerStatusColors[status]
  const logo = getLogo(offer.insurerId)

  const { mutateAsync: setIncluded } = useSetOfferIncluded()

  const iconSize = 4
  const { data: availableDiscounts, isFetched: isFetchedAvailableDiscounts, refetch: getOfferAvailableDiscounts } = useGetOfferAvailableDiscounts(offer.offerId)
  const filteredDiscounts = availableDiscounts?.data.availableDiscounts?.filter(x => x.editK2) ?? []
  const showDiscountsEditIcon = isFetchedAvailableDiscounts && filteredDiscounts.length > 0 && !printMode
  const discountText = offer.discounts.map(x => `${x.name} ${x.discount}${x.unit}`).join(', ')

  const handleToggle = ((included: boolean) => {
    onToggle?.(included)

    setTimeout(() => {
      offer!.included = included
      updateOffer(offer!)
      setIncluded({ offerId: offer.offerId!, included: included })
    }, 300)
  })

  const saveCurrentState = () => {
    setLoading(true)

    saveOfferParts(offer)
      .then(() =>
        recalculate([offer.offerId], true)
          .finally(() => {
            scrollToOffer(offer.offerId)

            setTimeout(() => {
              startAnimation()
            }, 500)

            setLoading(false)
          })
      )
      .catch(() => setLoading(false))
  }

  const { refetch: getOfferDocuments, data: offerDocuments } = useGetOfferDocuments(offer!.offerId, { enabled: false })
  const { data: dataOfferAvailablePackageItems, refetch: getOfferAvailablePackageItems } = useGetOfferAvailablePackageItems(offer!.offerId)
  const { data: offerAvailableAddons } = useGetOfferAvailableAddons(offer!.offerId)
  const packageItems = (dataOfferAvailablePackageItems?.data ?? []) as PackageItemModel[]

  // doctu nove dokumenty po kazde rekalkulaci
  useEffect(() => {
    setLoading(isRecalculating)

    if (!isRecalculating) {
      // je pred timeoutem, aby se mi po 5 vterinach nezmenily limity apod. Ale aby se to projevilo hned
      getOfferAvailablePackageItems() // i limity v balíčcích se mohou změnit vlivem změny jiných offer partů (vedlejší stavby, cennosti, ...)

      setTimeout(() => {
        getOfferDocuments() // at nebrani nacteni jinym dulezitejsim requestum
        getOfferAvailableDiscounts() // slevy se mohou po rekalkulaci zmenit
      }, 5000)
    }

  }, [isRecalculating])

  const offerDoc = offerDocuments?.data.find(x => x.name === 'OfferDocument')
  const xmlDocs = offerDocuments?.data.filter(x => x.name !== 'OfferDocument')
  const changed = useMemo(() => offer.parts.findIndex(x => !!x.changed) >= 0, [offer])

  useEffect(() => {
    if (changed)
      saveCurrentState()
  }, [changed])

  const [showSettingTab, setShowSettingTab] = useState<OfferPackageSettingsFormTabs | null>(null)
  const onOpenSettings = (showTab: OfferPackageSettingsFormTabs) => {
    setShowSettingTab(showTab)
    onOpen()
  }

  const productPrefix = GetWizardProductPrefix(product!)
  const selectOffer = () => {
    setIsSubmitting(true)

    selectCalculation({ paramsId: insuranceParamsId!, offerId: offer.offerId })
      .then(async () => {
        if ((insuranceParams?.generalParams.lastWizardStep ?? 2) < 3) {
          await updateStep({ paramsId: Number(insuranceParamsId!), step: 3 })
          await refetchInsuranceParams()
        }
        else
          await refetchInsuranceParams()

        navigate(`${productPrefix}/steps/3/` + insuranceParamsId!)
      })
      .finally(() => setIsSubmitting(false))
  }

  const partsWithLimitWarnings = offer.parts.filter(x => x.limitWarning)
  const handleSelectOfferClick = () => {
    if (partsWithLimitWarnings.length > 0)
      onOpenConfirmModal()
    else
      selectOffer()
  }

  if (!packageItems || packageItems.length == 0)
    return null

  const responsibility = insuranceGroups['0']?.find((x) => x.id == Risk.RESPONSIBILITY)
  const responsibilityRealestate = insuranceGroups['0']?.find((x) => x.id == Risk.RESPONSIBILITY_REALESTATE)
  const spolProperty = insuranceGroups['0']?.find((x) => x.id == Risk.SPOL_PROPERTY)
  const spolFlood = insuranceGroups['0']?.find((x) => x.id == Risk.SPOL_FLOOD)
  const spolResponsibility = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_RESPONSIBILITY)

  let responsibilityRealestateOfferPart = offer.parts.find((op) => op.riskId == Risk.RESPONSIBILITY_REALESTATE)
  const responsibilityRealestatePackageItem = packageItems.find((x) => x.riskId == Risk.RESPONSIBILITY_REALESTATE)
  let responsibilityOfferPart = offer.parts.find((op) => op.riskId == Risk.RESPONSIBILITY)
  const responsibilityPackageItem = packageItems.find((x) => x.riskId == Risk.RESPONSIBILITY)
  const spolPropertyOfferParts = offer.parts.filter((op) => op.riskId == Risk.SPOL_PROPERTY)
  const spolPropertyPackageItems = packageItems.filter((x) => x.riskId == Risk.SPOL_PROPERTY)!
  let spolPropertyOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_PROPERTY)
  const spolPropertyPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_PROPERTY)
  const spolFloodOfferParts = offer.parts.filter((op) => op.riskId == Risk.SPOL_FLOOD)
  const spolFloodPackageItems = packageItems.filter((x) => x.riskId == Risk.SPOL_FLOOD)!
  const floodRiskIncluded = offer.parts.some((op) => op.riskId == Risk.FLOOD && op.included)
  let spolFloodOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_FLOOD)
  const spolFloodPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_FLOOD)!
  let spolResponsibilityOfferPart = offer.parts.find((op) => op.riskId == Risk.SPOL_RESPONSIBILITY)
  const spolResponsibilityPackageItem = packageItems.find((x) => x.riskId == Risk.SPOL_RESPONSIBILITY)

  if (!responsibilityOfferPart && (isProperty || isLiability)) {
    responsibilityOfferPart = {
      partId: 0,
      riskId: Risk.RESPONSIBILITY,
      offerId: offer.offerId,
      packageItem: responsibilityPackageItem,
    } as PartPackageProperty

    offer.parts.push(responsibilityOfferPart)
  }

  if (!responsibilityRealestateOfferPart && (isProperty || isLiability)) {
    responsibilityRealestateOfferPart = {
      partId: 0,
      riskId: Risk.RESPONSIBILITY_REALESTATE,
      offerId: offer.offerId,
      packageItem: responsibilityRealestatePackageItem,
    } as PartPackageProperty

    offer.parts.push(responsibilityRealestateOfferPart)
  }

  if (!spolPropertyOfferPart && isProperty) {
    spolPropertyOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_PROPERTY,
      offerId: offer.offerId,
      packageItem: spolPropertyPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolPropertyOfferPart)
  }

  if (!spolFloodOfferPart && isProperty) {
    spolFloodOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_FLOOD,
      offerId: offer.offerId,
      packageItem: spolFloodPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolFloodOfferPart)
  }

  if (!spolResponsibilityOfferPart && (isProperty || isLiability)) {
    spolResponsibilityOfferPart = {
      partId: 0,
      riskId: Risk.SPOL_RESPONSIBILITY,
      offerId: offer.offerId,
      packageItem: spolResponsibilityPackageItem,
    } as PartPackageProperty

    offer.parts.push(spolResponsibilityOfferPart)
  }

  const itemPerGroupKey = Object.keys(insuranceGroups ?? {})
    .map(key => {
      const items = insuranceGroups[key]
        .filter(
          (x) =>
            ![
              Risk.RESPONSIBILITY,
              Risk.RESPONSIBILITY_REALESTATE,
              Risk.SPOL_PROPERTY,
              Risk.SPOL_FLOOD,
              Risk.SPOL_RESPONSIBILITY,
            ].includes(x.id),
        )

      return { items, key }
    })
    .filter(x => x.items.length > 0)

  const appliedAddons = offer.parts.filter(x => x.riskType == 4 && x.included)

  const exludedFromPrint = !offer.print
  const title = product == Product.TravelInsurance
    ? offerTravel.title
    : [
      ...new Set([
        offerProperty?.householdPackageName,
        offerProperty?.realtyPackageName,
        //offerProperty.liabilityPackageName,
      ].filter(x => !!x))].join(' + ')

  return (
    <>
      <Box
        onClick={() => {
          if (!printMode) return

          offer.print = !offer.print
          updateOffer(offer)
        }}
        id={getQuoteId(offer.offerId)}
        sx={{
          display: 'grid',
          gap: 'px',
          placeContent: 'stretch',
          borderLeft: '1px solid',
          borderLeftColor: printMode && !exludedFromPrint ? `${theme.colors.green[600]}` : 'gray.300',
          minW: 250,
          cursor: printMode ? 'pointer' : undefined,
          border: printMode && !exludedFromPrint ? `1px solid ${theme.colors.green[600]}` : undefined,
          mr: printMode && !exludedFromPrint ? '2px' : undefined,
        }}
      >
        {printMode && !exludedFromPrint && (
          <Badge
            colorScheme="green"
            variant="solid"
            sx={{
              position: 'absolute',
              zIndex: '1', // pridano kvuli prekryti loading boxem
              top: -4,
              //left: -0,
              left: '45%',
              boxShadow: 'md',
              transform: 'rotate(-0deg)',
            }}
          // {...rest}
          >
            <CustomTooltip label="Zahrnuto k tisku">
              <Check />
            </CustomTooltip>
          </Badge>
        )}

        {/* id, included... */}
        <OfferColumnSection
          sx={{
            px: 2,
            py: 1,
            bg: color,
          }}
        >
          <Flex sx={{ gap: 2 }} alignContent='center'>
            <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>{`#${displayId}`}</Text>
            {status && !printMode && <StatusBadge status={status} tooltip={errMsg} />}
          </Flex>

          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <Switch
              id={`offer-toggle-${displayId}`}
              size="sm"
              isDisabled={isDisabled || printMode}
              isChecked={offer?.included}
              onChange={(e) => handleToggle(e.target.checked)}
            />

            {/* <Flex>
              <IconButton bg="transparent" aria-label="Posunout doleva">
                <ChevronLeft width={3} height={3} />
              </IconButton>
              <IconButton bg="transparent" aria-label="Posunout doprava">
                <ChevronRight width={3} height={3} />
              </IconButton>
            </Flex> */}
          </Flex>
        </OfferColumnSection>

        {/* logo */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
            py: 0,
            height: '52px'
          }}
        >
          <Image
            src={logo}
            alt={offer.insurerName}
            width="64px"
            maxH="48px"
            style={{ mixBlendMode: 'multiply' }}
          />

          {/* {changed && (
            <Button colorScheme="blue" size="sm" onClick={saveCurrentState}>
              <Refresh />
              Přepočítat
            </Button>
          )} */}

          {status === OfferStatusEnum.Error && (
            <CustomTooltip label="Nabídku se nepodařilo zkalkulovat" hasArrow placement="top" trigger='clickHover'>
              <Text sx={{ color: 'red.800', fontSize: 'xs', fontWeight: 'semibold' }}>Chyba</Text>
            </CustomTooltip>
          )}
        </OfferColumnSection>

        {/* price */}
        <OfferColumnSection
          sx={{
            p: 2,
            bg: color,
          }}
        >
          {isLiability &&
            <EditableTinySelect
              variants={availablePackages}
              isDisabled={isDisabled || !hasAnyPackageToChange}
              onSubmit={(value: string, id: number) => {
                setCurrentLiabilityPackageId(id)
                updatePackage({ offerId: offer.offerId, packages: [{ packageId: id, groupId: 5 }] })
                  .then(() => {
                    recalculate([offer.offerId], true)
                      .then(() => { getOfferAvailablePackageItems() })
                  })
              }}
              value={currentLiabilityPackageId?.toString() ?? ''}
            />
          }

          {!hasAnyPackageToChange
            ?
            <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>
              {title}
            </Text>
            :
            <Flex gap={1}>
              <Text sx={{ fontSize: 'sm', fontWeight: 'medium', cursor: 'pointer' }} onClick={(e) => onOpenSettings(OfferPackageSettingsFormTabs.packages)}>
                {title}
              </Text>
            </Flex>
          }


          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {changed ? '???' : formatPrice({ price: offer.price })}
          </Text>
        </OfferColumnSection>

        {/* buttons */}
        {!printMode &&
          <OfferColumnSection
            sx={{
              gap: 8,
              px: 2,
            }}
          >
            {/* <Stars /> */}

            <Flex sx={{ gap: 1, flex: 1, justifyContent: 'end', alignItems: 'center' }}>
              {(xmlDocs && xmlDocs.length > 0) && user?.enableOfferXml &&
                <CustomTooltip
                  trigger='click'
                  hasArrow
                  label={(
                    <Stack gap={3} py={2}>
                      {xmlDocs.map(doc => (
                        <Link
                          key={doc.id}
                          as={'a'}
                          target='_blank'
                          href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}
                          _notLast={{
                            borderBottom: '1px solid',
                            borderColor: 'gray.200'
                          }}
                        >
                          {doc.name} ({formatDateTime(doc.date, 'D.M.YYYY HH:mm:ss.SSS')})
                        </Link>
                      ))}
                    </Stack>
                  )}>
                  <Button variant='ghost' px={0}>
                    XML
                  </Button>
                </CustomTooltip>
              }

              {offerDoc &&
                <Tooltip label="Stáhnout PDF nabídku pojišťovny" hasArrow placement="top">
                  <IconButton variant="ghost" aria-label="PDF" as={'a'} target='_blank' href={`${documentUrl}/GetOfferDocument?docId=${offerDoc.id}`}>
                    <Pdf color="secondary.500" />
                  </IconButton>
                </Tooltip>
              }

              {!isDisabled &&
                <>
                  <Tooltip label="Přepočítat nabídku" hasArrow placement="top">
                    <IconButton variant="ghost" aria-label="Obnovit"
                      onClick={() => {
                        recalculate([offer.offerId], true)
                          .finally(() => {
                            getOfferDocuments()
                            startAnimation()
                          })
                      }}>
                      <Refresh color="secondary.500" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip label="Klonovat nabídku" hasArrow placement="top">
                    <IconButton variant="ghost" aria-label="Klonovat"
                      onClick={() => {
                        cloneOffer()
                          .then(({ data: offer }) => {
                            updateOffer(offer as OfferPackage)
                            recalculate([offer.offerId], true)
                          })
                      }}
                    >
                      <Duplicate color="secondary.500" />
                    </IconButton>
                  </Tooltip>

                </>
              }

              {offerAvailableAddons?.data && offerAvailableAddons.data.length > 0 &&
                <Tooltip
                  label={
                    <Stack>
                      <Text>Další doplňková pojištění</Text>
                      {appliedAddons.length > 0 &&
                        <>
                          <Divider></Divider>
                          <Text fontSize='sm' fontWeight='semibold'>Vybraná připojištění</Text>
                          <UnorderedList fontSize='small'>
                            {appliedAddons.map((x) => <ListItem key={x.riskId}>{x.name}</ListItem>)}
                          </UnorderedList>
                        </>
                      }
                    </Stack>
                  }
                  hasArrow
                  placement="top"
                >
                  <IconButton variant="ghost" aria-label="Nastavení" onClick={() => onOpenSettings(OfferPackageSettingsFormTabs.addons)}>
                    <Settings
                      color={`${appliedAddons.length > 0 ? 'yellow.500' : 'secondary.500'}`}
                    />
                  </IconButton>
                </Tooltip>
              }

            </Flex>
          </OfferColumnSection>
        }

        {/* risks in groups */}
        {itemPerGroupKey && itemPerGroupKey.length > 0 && itemPerGroupKey.map((x) => {
          const items = x.items
          const section = items[0]

          let titleHouseholdAndRealty: React.ReactNode = null
          if (section.groupId == 0) {
            const ids = items.map((x) => x.id)
            let isHouseholdAndRealty = false

            ids.forEach((riskId) => {
              if (offer.parts.filter((op) => op.packageItem?.riskId == riskId).length > 1) {
                isHouseholdAndRealty = true
              }
            })
            if (isHouseholdAndRealty) {
              titleHouseholdAndRealty = (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    justifyContent: 'end',
                    width: '100%',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>Domácnost</Box>
                  <Box sx={{ textAlign: 'center' }}>Stavba</Box>
                </Box>
              )
            }
          }

          return (
            <OfferPackageColumnSection
              onClick={(e) => {
                onSectionTitleClick(section.groupId)
                e.stopPropagation() // kvuli print mode - aby se pri otevirani neprepinalo vybrani k tisku
              }}
              isOpened={openedColumns.includes(section.groupId)}
              key={section.groupId}
              titleBg={color}
              bodyCells={items.map(x => {
                const filtered = packageItems.filter(pi => pi.riskId == x.id) ?? []
                // if (filtered.length == 0) {
                //   console.log('missing package items:', offer.insurerName, offer.title, x.id, offer.offerId)
                // }

                return filtered
                  .map(pi => {
                    // const opX = offer.parts.filter(op => !op.packageItem)
                    // if (opX.length > 0)
                    //   console.log('missing package item:', offer, opX);

                    let op = offer.parts.find(op => op.packageItem?.packageItemId == pi.packageItemId)
                    if (!op) {
                      op = {
                        partId: 0,
                        riskId: x.id,
                        offerId: offer.offerId,
                        packageItem: pi
                      } as PartPackage

                      offer.parts.push(op)
                    }

                    return {
                      id: x.id.toString(),
                      offerId: offer.offerId,
                      offerPart: op,
                      packageItem: pi
                    } as IBodyCell
                  })
              })}
            />
          )
        })}

        {responsibility && responsibilityPackageItem
          ? (
            <OfferCell
              id={Risk.RESPONSIBILITY.toString()}
              offerPart={responsibilityOfferPart!}
              packageItem={responsibilityPackageItem}
            />
          ) :
          (isProperty || isLiability) && <BlankOfferCell riskId={Risk.RESPONSIBILITY} />
        }

        {responsibilityRealestate && responsibilityRealestatePackageItem
          ? (
            <OfferCell
              id={Risk.RESPONSIBILITY_REALESTATE.toString()}
              offerPart={responsibilityRealestateOfferPart!}
              packageItem={responsibilityRealestatePackageItem}
            />
          ) :
          (isProperty || isLiability) && <BlankOfferCell riskId={Risk.RESPONSIBILITY_REALESTATE} />
        }

        {spolProperty && spolPropertyOfferParts.length > 1 && (
          <Box
            key={'spolBox'}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${spolPropertyOfferParts.length}, 1fr)`,
              justifyContent: 'end',
            }}
          >
            {(spolPropertyOfferParts as PartPackageProperty[])
              .sort(
                (a, b) =>
                  a.packageItem.productTypeRiskVariantId! - b.packageItem.productTypeRiskVariantId!,
              )
              .map((spolOfferPart, i) => {
                const spolPropertyPackageItem = spolPropertyPackageItems.find(
                  (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                )

                return spolPropertyPackageItem ? (
                  <OfferCell
                    key={i}
                    id={Risk.SPOL_PROPERTY.toString()}
                    offerPart={spolOfferPart}
                    packageItem={
                      spolPropertyPackageItems.find(
                        (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                      )!
                    }
                    singleCell={false}
                  />
                ) : undefined
              })}
          </Box>
        )}

        {spolProperty && spolPropertyOfferParts.length == 1 && spolPropertyPackageItem && (
          <OfferCell
            id={Risk.SPOL_PROPERTY.toString()}
            offerPart={spolPropertyOfferPart!}
            packageItem={spolPropertyPackageItem}
          />
        )}

        {spolFlood && floodRiskIncluded && spolFloodOfferParts.length > 1 && (
          <Box
            key={'spolFoodBox'}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${spolFloodOfferParts.length}, 1fr)`,
              justifyContent: 'end',
            }}
          >
            {(spolFloodOfferParts as PartPackageProperty[])
              .sort(
                (a, b) =>
                  a.packageItem.productTypeRiskVariantId! - b.packageItem.productTypeRiskVariantId!,
              )
              .map((spolOfferPart, i) => {
                const spolFloodPackageItem = spolFloodPackageItems.find(
                  (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                )

                return spolFloodPackageItem ?
                  <OfferCell
                    key={i}
                    id={Risk.SPOL_FLOOD.toString()}
                    offerPart={spolOfferPart}
                    packageItem={
                      spolFloodPackageItems.find(
                        (pi) => pi.packageItemId == spolOfferPart.packageItem.packageItemId,
                      )!
                    }
                    singleCell={false}
                  /> : undefined
              })}
          </Box>
        )}

        {spolFlood && floodRiskIncluded && spolFloodOfferParts.length == 1 && (
          <OfferCell
            id={Risk.SPOL_FLOOD.toString()}
            offerPart={spolFloodOfferPart!}
            packageItem={spolFloodPackageItem}
          />
        )}

        {/* pokud nabídka neobsahuje povodeň, zobrazujeme pouze prázdnou buňku */}
        {spolFlood && !floodRiskIncluded && isProperty && (
          <BlankOfferCell riskId={Risk.SPOL_FLOOD} />
        )}

        {spolResponsibility &&
          <>
            {spolResponsibilityPackageItem && spolResponsibilityOfferPart ? (
              <OfferCell
                id={Risk.SPOL_RESPONSIBILITY.toString()}
                offerPart={spolResponsibilityOfferPart}
                packageItem={spolResponsibilityPackageItem}
              />
            )
              : <BlankOfferCell riskId={Risk.SPOL_RESPONSIBILITY} />
            }
          </>
        }

        {/* <DiscountCell offer={offer} isDisabled={isDisabled} /> */}
        <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', mr: isDisabled && !printMode ? 5 : 0 }} riskId='discount'>
          <Text sx={{ p: 0, fontSize: 'sm', mr: !isDisabled && !showDiscountsEditIcon ? 5 : 0 }}>
            {!!discountText && discountText}
            {!discountText && 'Bez slevy'}
          </Text>

          {showDiscountsEditIcon &&
            <IconButton
              variant="ghost"
              colorScheme="white"
              aria-label="Upravit limit"
              onClick={(e) => onOpenSettings(OfferPackageSettingsFormTabs.discounts)}
            >
              <EditFilled color="gray.500" width={iconSize} height={iconSize} />
            </IconButton>
          }
        </OfferCellWrapper>

        {[Product.PropertyInsurance, Product.LiabilityInsurance].includes(product!) &&
          <OfferCellPayment offerId={offer.offerId} isDisabled={isDisabled} />
        }

        <OfferColumnSection h='52px'>
          {status !== OfferStatusEnum.Error && !isDisabled && !changed && (

            <Button
              w={'100%'}
              colorScheme="green"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              loadingText="Ukládám..."
              onClick={handleSelectOfferClick}
            >
              <Check />

              Pokračovat
            </Button>

          )}
        </OfferColumnSection>
      </Box >

      <OfferPackageSettingsForm
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={() => recalculate([offer.offerId])}
        offer={offer}
        title={`Nastavení nabídky #${displayId}`}
        isReadOnly={isDisabled}
        activeTab={showSettingTab}
      />

      <ConfirmModal
        isOpen={isOpenConfirmModal}
        onClose={(result: boolean) => {
          onCloseConfirmModal()

          if (result)
            selectOffer()
        }}
        title='Potvrzení'
        yesButtonContent='Ano'
        noButtonContent='Ne'
        children={
          <Stack gap={4}>
            <Text>Některé parametry nabídky nesplňují Vaše požadavky limitů:</Text>

            <UnorderedList>
              {partsWithLimitWarnings.map((x, i) => <ListItem key={i}><strong>{x.name}:</strong> {x.limitWarning}</ListItem>)}
            </UnorderedList>

            <Text>Chcete přesto nabídku sjednat?</Text>
          </Stack>
        }
      />
    </>
  )
}